import { useAuth0 } from '@auth0/auth0-react'
import { Browser } from '@capacitor/browser'
import { useCallback } from 'react'
import { clearLocalStorage } from '../helpers/auth-helper'
import { useAppDispatch } from '../store'
import { clearData as authClearData } from '../store/authSlice'
import { clearData as eaterClearData } from '../store/eaterSlice'
import { clearData as landingClearData } from '../store/landingSlice'
import { clearData as onboardClearData } from '../store/onboardSlice'
import { clearData as profileClearData } from '../store/profileSlice'
import { clearData as restaurantClearData } from '../store/restaurantSlice'
import { clearData as searchClearData } from '../store/searchSlice'
import { clearData as socialClearData } from '../store/socialSlice'
import { clearData as menuClearData } from '../store/restaurant/menu'
import { clearData as settingClearData } from '../store/restaurant/setting'
import { clearData as dashboardClearData } from '../store/restaurant/dashboard'
import { clearData as reviewClearData } from '../store/restaurant/review'
import { clearData as messageClearData } from '../store/restaurant/messages'
import { clearData as campaignClearData } from '../store/influencer/campaign'
import { clearData as contentCreatorClearData } from '../store/influencer/contentCreator'
import { clearData as influencerClearData } from '../store/restaurant/influencer'
import { clearData as reservationClearData } from '../store/reservation'

import { useDevice } from './useDevice'
import { errorLog } from '../utils/log-helper'
import { logEvent, flushEventsBeforeNavigation } from '../analytics/analytics'

/**
 * Pairs a login attempt started here with its completion/failure in BaseLayout after Auth0
 * redirects back. sessionStorage survives the same-tab redirect round-trip.
 */
export const LOGIN_ATTEMPT_KEY = 'rsly_login_attempt'

interface AuthHelper {
  logoutAuthUser: () => void
  loginAuthUser: () => void
}

export const useAuthHelper = (): AuthHelper => {
  const { logout, loginWithRedirect } = useAuth0()
  const { isHybrid, isAndroid } = useDevice()
  const dispatch = useAppDispatch()

  // Combined login function for web and mobile
  const loginAuthUser = useCallback(() => {
    // Login funnel entry (top-right auth button). The outcome resolves in BaseLayout:
    // account_created (new user), login_completed (returning), or login_failed.
    try {
      sessionStorage.setItem(LOGIN_ATTEMPT_KEY, '1')
      logEvent('login_started', { screen_name: 'nav', source: 'home' })
      // This button always redirects to the Auth0 SIGNUP screen (screen_hint below), so it is also
      // the top of the signup funnel. signup_started was previously only fired from a few in-context
      // CTAs (save / menu-scan / dine), so the funnel under-counted signup intent ~10x. Emit it here
      // too — mode:'signup' distinguishes this from a returning-user login at analysis time.
      logEvent('signup_started', {
        screen_name: 'nav',
        source: 'auth_button',
        mode: 'signup',
      })
      // login_started is logged immediately before a full-page Auth0 redirect. Hand the queue to a
      // keepalive/beacon transport now so it survives the navigation — a normal async POST is
      // cancelled when the document is torn down, which lost most login_started events (breaking the
      // login→signup funnel where account_created out-ran login_started).
      flushEventsBeforeNavigation()
    } catch {
      // analytics must never block auth
    }
    if (isHybrid) {
      void loginWithRedirect({
        authorizationParams: { screen_hint: 'signup' },
        openUrl(url: string) {
          // Use Capacitor Browser plugin for mobile
          Browser.open({
            url,
            windowName: '_self',
          }).catch((err) => {
            errorLog('Failed to open browser:', err)
          })
        },
      })
    } else {
      // Web login
      loginWithRedirect({
        authorizationParams: { screen_hint: 'signup' },
      }).catch((err) => {
        errorLog('Login failed:', err)
      })
    }
  }, [isHybrid, loginWithRedirect])

  const logoutAuthUser = useCallback(() => {
    clearLocalStorage()
    dispatch(restaurantClearData())
    dispatch(authClearData())
    dispatch(eaterClearData())
    dispatch(landingClearData())
    dispatch(onboardClearData())
    dispatch(profileClearData())
    dispatch(searchClearData())
    dispatch(socialClearData())
    dispatch(menuClearData())
    dispatch(settingClearData())
    dispatch(dashboardClearData())
    dispatch(reviewClearData())
    dispatch(messageClearData())
    dispatch(campaignClearData())
    dispatch(contentCreatorClearData())
    dispatch(influencerClearData())
    dispatch(reservationClearData())

    const packageId =
      (isAndroid
        ? process.env.REACT_APP_ANDROID_APP_ID
        : process.env.REACT_APP_IOS_APP_ID) ?? ''

    if (isHybrid) {
      void logout({
        logoutParams: {
          returnTo: `${packageId}://login.resactly.com/capacitor/${packageId}/callback`,
        },
        openUrl(url: string) {
          // Redirect using Capacitor's Browser plugin
          Browser.open({
            url,
            windowName: '_self',
          }).catch((err) => {
            errorLog('Failed to open browser during logout:', err)
          })
        },
      })
    } else {
      logout({ logoutParams: { returnTo: window.location.origin } }).catch(
        (err) => {
          errorLog('Logout failed:', err)
        },
      )
    }
  }, [isHybrid, isAndroid])

  return { loginAuthUser, logoutAuthUser }
}
